/** Org-wide Missing materials inbox (Inventory desk surface). */
import { useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import type { MfgMaterial, MfgRecipePendingLine } from "@/lib/manufacturing/types";
import { MaterialSearchableSelect } from "./MaterialSearchableSelect";
import { useManufacturingInventoryMissing } from "./useManufacturingInventoryMissing";
import {
  adminDangerGhostBtnClass,
  ghostBtnClass,
  mfgInlineInputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";

type EditMode = "idle" | "pick" | "create";

function ownerKindLabel(row: MfgRecipePendingLine): string {
  const kind = row.owner_kind ?? "recipe";
  if (kind === "material_use") return "Part use";
  if (kind === "named_variant") return "Named variant";
  return "Recipe";
}

export function ManufacturingInventoryMissingBody({
  enabled,
  materials,
  onChanged,
}: {
  enabled: boolean;
  materials: ReadonlyArray<MfgMaterial>;
  onChanged?: () => void;
}) {
  const flow = useManufacturingInventoryMissing({ enabled, onChanged });
  const [rowError, setRowError] = useState<string | null>(null);

  return (
    <SectionCard
      title={`Missing materials (${flow.total})`}
      description="Placeholders from models, variants, and part uses waiting for a catalog match. One entry per material usage."
    >
      {flow.loading ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">Loading missing materials…</div>
      ) : null}
      {flow.error || rowError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200">
          {flow.error || rowError}
        </div>
      ) : null}
      {!flow.loading && flow.rows.length === 0 ? (
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          No missing material matches waiting.
        </p>
      ) : null}
      <div className="space-y-2">
        {flow.rows.map((row) => (
          <MissingRow
            key={row.id}
            row={row}
            materials={materials}
            busy={flow.busyId === row.id}
            onMatch={async (materialId) => {
              setRowError(null);
              const r = await flow.matchExisting(row.id, materialId);
              if (!r.ok) setRowError(r.error);
            }}
            onCreateAndMatch={async (input) => {
              setRowError(null);
              const r = await flow.createAndMatch(row.id, input);
              if (!r.ok) setRowError(r.error);
            }}
            onDiscard={async () => {
              setRowError(null);
              const r = await flow.discard(row.id);
              if (!r.ok) setRowError(r.error);
            }}
          />
        ))}
      </div>
    </SectionCard>
  );
}

function MissingRow({
  row,
  materials,
  busy,
  onMatch,
  onCreateAndMatch,
  onDiscard,
}: {
  row: MfgRecipePendingLine;
  materials: ReadonlyArray<MfgMaterial>;
  busy: boolean;
  onMatch: (materialId: string) => Promise<void>;
  onCreateAndMatch: (input: {
    name: string;
    category: string;
    unit: string;
  }) => Promise<void>;
  onDiscard: () => Promise<void>;
}) {
  const [mode, setMode] = useState<EditMode>("idle");
  const [newName, setNewName] = useState(row.material_name);
  const [newCategory, setNewCategory] = useState("MISC");
  const [newUnit, setNewUnit] = useState("each");

  const ownerBits = [ownerKindLabel(row), row.owner_code, row.owner_label]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 text-sm dark:border-amber-800 dark:bg-amber-900/15">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-medium text-gray-900 dark:text-zinc-100">{row.material_name}</span>
        <span className="text-xs text-gray-600 dark:text-zinc-300">
          qty {row.quantity}
          {row.slot_label ? ` · ${row.slot_label}` : ""}
        </span>
        {ownerBits ? (
          <span className="text-xs text-amber-900 dark:text-amber-200">{ownerBits}</span>
        ) : null}
      </div>
      {row.notes ? (
        <p className="mt-1 text-xs text-gray-700 dark:text-zinc-300">{row.notes}</p>
      ) : null}
      <div className="mt-2 flex flex-wrap gap-2">
        <button
          type="button"
          className={ghostBtnClass}
          onClick={() => setMode(mode === "pick" ? "idle" : "pick")}
          disabled={busy}
        >
          {mode === "pick" ? "Cancel pick" : "Pick existing"}
        </button>
        <button
          type="button"
          className={ghostBtnClass}
          onClick={() => setMode(mode === "create" ? "idle" : "create")}
          disabled={busy}
        >
          {mode === "create" ? "Cancel create" : "Create new material"}
        </button>
        <button
          type="button"
          className={adminDangerGhostBtnClass}
          onClick={() => void onDiscard()}
          disabled={busy}
        >
          {busy ? "Working…" : "Discard"}
        </button>
      </div>

      {mode === "pick" ? (
        <div className="mt-2 space-y-1">
          <MaterialSearchableSelect
            materials={materials}
            initialQuery={row.material_name}
            disabled={busy}
            autoFocus
            onSelect={(m) => void onMatch(m.id)}
          />
        </div>
      ) : null}

      {mode === "create" ? (
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
          <label className="text-xs text-gray-600 dark:text-zinc-300">
            Name
            <input
              className={`${mfgInlineInputClass} mt-1 w-full`}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
          </label>
          <label className="text-xs text-gray-600 dark:text-zinc-300">
            Category
            <input
              className={`${mfgInlineInputClass} mt-1 w-full`}
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value.toUpperCase())}
            />
          </label>
          <label className="text-xs text-gray-600 dark:text-zinc-300">
            Unit
            <input
              className={`${mfgInlineInputClass} mt-1 w-full`}
              value={newUnit}
              onChange={(e) => setNewUnit(e.target.value)}
            />
          </label>
          <div className="sm:col-span-3">
            <button
              type="button"
              className={mfgPrimaryBtnClass}
              disabled={!newName.trim() || !newCategory.trim() || !newUnit.trim() || busy}
              onClick={() =>
                void onCreateAndMatch({
                  name: newName.trim(),
                  category: newCategory.trim(),
                  unit: newUnit.trim(),
                })
              }
            >
              {busy ? "Creating…" : "Create & match"}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
